import { supabase } from "@/integrations/supabase/client";
import { Community, CommunityWithDetails, CommunityPost } from "@/types/community";

// ===============================
// SERVIÇOS PARA MENTORES - COMUNIDADES
// ===============================

export interface CreateCommunityData {
  name: string;
  slug: string;
  description?: string;
  cover_image_url?: string;
  rules?: string;
  game_id: string;
  is_featured?: boolean;
}

export interface UpdateCommunityData {
  name?: string;
  slug?: string;
  description?: string;
  cover_image_url?: string;
  rules?: string;
  game_id?: string;
  is_featured?: boolean;
  is_active?: boolean;
}

// ===============================
// BUSCAR COMUNIDADES DO MENTOR
// ===============================

export const fetchMentorCommunities = async (mentorId: string): Promise<CommunityWithDetails[]> => {
  console.log('🔍 [fetchMentorCommunities] Buscando comunidades para mentor:', mentorId);
  
  try {
    // Query simples sem joins problemáticos
    console.log('🔄 [fetchMentorCommunities] Fazendo query simples...');
    
    const { data, error } = await supabase
      .from('communities')
      .select('*')
      .eq('mentor_id', mentorId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ [fetchMentorCommunities] Erro na query:', error);
      
      // Se mentor_id não funcionar, tentar owner_id
      console.log('🔄 [fetchMentorCommunities] Tentando com owner_id...');
      const { data: ownerData, error: ownerError } = await supabase
        .from('communities')
        .select('*')
        .eq('owner_id', mentorId)
        .order('created_at', { ascending: false });

      if (ownerError) {
        console.error('❌ [fetchMentorCommunities] Ambos os campos falharam:', { error, ownerError });
        throw new Error(`Falha ao acessar comunidades: ${error.message}`);
      }

      console.log('✅ [fetchMentorCommunities] Sucesso com owner_id. Comunidades:', ownerData?.length || 0);
      
      // Buscar dados do mentor para owner_id fallback
      const mentorProfile = await fetchMentorProfile(mentorId);
      
      return (ownerData?.map(community => ({
        ...community,
        game: null,
        mentor: mentorProfile
      })) as CommunityWithDetails[]) || [];
    }

    console.log('✅ [fetchMentorCommunities] Comunidades encontradas:', data?.length || 0);
    console.log('📋 [fetchMentorCommunities] Dados das comunidades:', data);

    // Buscar dados dos jogos separadamente
    console.log('🎮 [fetchMentorCommunities] Buscando dados dos jogos:', data.map(c => c.game_id));
    
    const gameIds = [...new Set(data.map(c => c.game_id).filter(Boolean))];
    console.log('🔍 [fetchMentorCommunities] Total de IDs únicos de jogos:', gameIds.length);
    console.log('🔍 [fetchMentorCommunities] IDs dos jogos:', gameIds);

    // Buscar dados dos jogos
    const games = gameIds.length > 0 ? await fetchGamesByIds(gameIds) : [];
    
    // Buscar dados do mentor
    const mentor = await fetchMentorProfile(mentorId);

    // Combinar dados
    const result: CommunityWithDetails[] = data.map(community => ({
      ...community,
      game: games.find(g => g.id === community.game_id) || {
        id: community.game_id,
        name: 'Jogo Desconhecido'
      },
      mentor: mentor
    }));

    console.log('✅ [fetchMentorCommunities] Comunidades com dados dos jogos e mentor:', result.length);
    return result;
  } catch (error) {
    console.error('❌ [fetchMentorCommunities] Erro geral:', error);
    throw error;
  }
};

// Função auxiliar para buscar dados do mentor
const fetchMentorProfile = async (mentorId: string) => {
  console.log('👤 [fetchMentorProfile] Buscando dados do mentor:', mentorId);
  
  try {
    // Buscar apenas campos que existem na tabela profiles
    const { data: mentorData, error } = await supabase
      .from('profiles')
      .select('id, nickname, slug')
      .eq('id', mentorId)
      .single();

    if (error) {
      console.warn('⚠️ [fetchMentorProfile] Erro ao buscar perfil completo (tentando campos básicos):', error.message);
      
      // Tentar apenas com campos básicos
      const { data: basicData, error: basicError } = await supabase
        .from('profiles')
        .select('id, nickname')
        .eq('id', mentorId)
        .single();
      
      if (basicError) {
        console.warn('⚠️ [fetchMentorProfile] Erro mesmo com campos básicos:', basicError.message);
        return {
          id: mentorId,
          nickname: 'Mentor',
          slug: null,
          avatar_url: null
        };
      }
      
      return {
        ...basicData,
        avatar_url: null
      };
    }

    // Buscar imagem do mentor com múltiplos fallbacks
    console.log('🖼️ [fetchMentorProfile] Buscando imagem do mentor...');
    let mentorImageUrl = null;
    
    try {
      // 1. Tentar buscar na tabela mentor_info primeiro
      const { data: mentorInfoData, error: mentorInfoError } = await supabase
        .from('mentor_info')
        .select('image_url')
        .eq('profile_id', mentorId)
        .maybeSingle();
      
      if (!mentorInfoError && mentorInfoData?.image_url) {
        if (mentorInfoData.image_url.startsWith('http')) {
          mentorImageUrl = mentorInfoData.image_url;
        } else {
          // Se é apenas o nome do arquivo, formar caminho completo
          let fullPath = mentorInfoData.image_url;
          if (!mentorInfoData.image_url.includes('/')) {
            fullPath = `${mentorId}/${mentorInfoData.image_url}`;
          }
          
          const { data: urlData } = supabase.storage
            .from('profile-images')
            .getPublicUrl(fullPath);
          mentorImageUrl = urlData.publicUrl;
        }
        console.log('📸 [fetchMentorProfile] Imagem encontrada em mentor_info:', mentorImageUrl);
      }
      
      // 2. Se não encontrou, tentar buscar no campo image_url da tabela profiles
      if (!mentorImageUrl) {
        const { data: profileImageData, error: profileImageError } = await supabase
          .from('profiles')
          .select('image_url')
          .eq('id', mentorId)
          .single();
        
        if (!profileImageError && profileImageData?.image_url) {
          mentorImageUrl = profileImageData.image_url;
          console.log('📸 [fetchMentorProfile] Imagem encontrada na tabela profiles:', mentorImageUrl);
        }
      }
      
      // 3. Se ainda não encontrou, tentar buscar no storage
      if (!mentorImageUrl) {
        const { data: imageFiles, error: storageError } = await supabase
          .storage
          .from('profile-images')
          .list(mentorId);
        
        if (!storageError && imageFiles && imageFiles.length > 0) {
          // Buscar o arquivo mais recente que comece com 'profile-'
          const profileImages = imageFiles
            .filter(file => file.name.startsWith('profile-'))
            .sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime());
          
          if (profileImages.length > 0) {
            mentorImageUrl = supabase.storage
              .from('profile-images')
              .getPublicUrl(`${mentorId}/${profileImages[0].name}`)
              .data.publicUrl;
            
            console.log('📸 [fetchMentorProfile] Imagem mais recente encontrada no storage:', mentorImageUrl);
          }
        }
      }
    } catch (imageError) {
      console.warn('⚠️ [fetchMentorProfile] Erro ao buscar imagem do mentor:', imageError);
    }

    console.log('✅ [fetchMentorProfile] Perfil encontrado:', mentorData);
    return {
      ...mentorData,
      avatar_url: mentorImageUrl
    };
  } catch (error) {
    console.error('❌ [fetchMentorProfile] Erro inesperado:', error);
    return {
      id: mentorId,
      nickname: 'Mentor',
      slug: null,
      avatar_url: null
    };
  }
};

// Nova função auxiliar para buscar jogos com tratamento de erro
const fetchGamesByIds = async (gameIds: string[]) => {
  console.log('🎮 [fetchGamesByIds] Buscando jogos por IDs:', gameIds);
  
  try {
    // Tentar com campos básicos primeiro
    const { data: gamesData, error } = await supabase
      .from('games')
      .select('id, name, cover_url')
      .in('id', gameIds);

    if (error) {
      console.warn('⚠️ [fetchGamesByIds] Erro com campos básicos (tentando só nome):', error.message);
      
      // Tentar só com id e name
      const { data: altGamesData, error: altError } = await supabase
        .from('games')
        .select('id, name')
        .in('id', gameIds);
      
      if (altError) {
        console.error('❌ [fetchGamesByIds] Erro com campos alternativos:', altError.message);
        return [];
      }
      
      console.log('✅ [fetchGamesByIds] Jogos encontrados (campos básicos):', altGamesData?.length || 0);
      return altGamesData || [];
    }

    console.log('✅ [fetchGamesByIds] Jogos encontrados:', gamesData?.length || 0);
    return gamesData || [];
    
  } catch (error: any) {
    console.error('❌ [fetchGamesByIds] Erro geral:', error);
    return [];
  }
};

// Função auxiliar para validar UUID
const isValidUUID = (uuid: string): boolean => {
  if (!uuid || typeof uuid !== 'string') {
    console.warn('⚠️ [isValidUUID] UUID inválido (não é string):', uuid);
    return false;
  }
  
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const isValid = uuidRegex.test(uuid);
  
  if (!isValid) {
    console.warn('⚠️ [isValidUUID] UUID com formato inválido:', uuid);
  }
  
  return isValid;
};

// ===============================
// CRIAR NOVA COMUNIDADE
// ===============================

export const createCommunity = async (
  mentorId: string, 
  communityData: CreateCommunityData
): Promise<Community> => {
  console.log('🎯 [createCommunity] Iniciando criação da comunidade:', { mentorId, communityData });
  
  try {
    // Verificar se o slug já existe e gerar um único se necessário
    console.log('🔍 [createCommunity] Verificando disponibilidade do slug:', communityData.slug);
    
    let finalSlug = communityData.slug;
    const isSlugAvailable = await validateCommunitySlug(finalSlug);
    
    if (!isSlugAvailable) {
      console.warn('⚠️ [createCommunity] Slug já existe, gerando um único...');
      finalSlug = await generateUniqueSlug(communityData.name);
      console.log('✅ [createCommunity] Novo slug gerado:', finalSlug);
    }

    // Criar payload base sempre incluindo mentor_id E owner_id (ambos obrigatórios)
    console.log('🔄 [createCommunity] Criando payload base...');
    
    const basePayload = {
      ...communityData,
      slug: finalSlug, // Usar o slug único gerado
      mentor_id: mentorId, // SEMPRE incluir mentor_id (obrigatório)
      owner_id: mentorId, // SEMPRE incluir owner_id (obrigatório - mesmo valor que mentor_id)
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_active: true,
      member_count: 0,
      post_count: 0,
      like_count: 0,
      comment_count: 0,
      // Usar cover_image_url diretamente
      cover_image_url: communityData.cover_image_url
    };
    
    // Não remover o campo cover_image_url, apenas garantir que não há duplicação
    delete basePayload.cover_image_url;
    if (communityData.cover_image_url) {
      basePayload.cover_image_url = communityData.cover_image_url;
    }

    console.log('📋 [createCommunity] Payload base:', basePayload);

    // Tentar criar a comunidade
    const { data, error } = await supabase
      .from('communities')
      .insert(basePayload)
      .select()
      .single();

    if (error) {
      console.error('❌ [createCommunity] Erro ao criar:', error);
      
      // Tratamento específico para erro 409 (conflito)
      if (error.code === '23505' || error.message?.includes('duplicate key')) {
        throw new Error('Já existe uma comunidade com esta URL ou nome');
      }
      
      // Tratamento para constraint violations
      if (error.code === '23502' || error.message?.includes('not-null constraint')) {
        throw new Error('Dados obrigatórios faltando. Verifique se todos os campos estão preenchidos.');
      }
      
      // Outros erros
      throw new Error(`Falha ao criar comunidade: ${error.message}`);
    }

    console.log('🎉 [createCommunity] Comunidade criada com sucesso:', data);
    return data as Community;
    
  } catch (error) {
    console.error('❌ [createCommunity] Erro geral:', error);
    throw error;
  }
};

// ===============================
// ATUALIZAR COMUNIDADE
// ===============================

export const updateCommunity = async (
  communityId: string,
  mentorId: string,
  updateData: UpdateCommunityData
): Promise<Community> => {
  console.log('🔧 [updateCommunity] Atualizando comunidade:', communityId, 'mentor:', mentorId);
  
  try {
    // Primeiro buscar a comunidade pelo ID para verificar permissões
    const { data: communityData, error: findError } = await supabase
      .from('communities')
      .select('id, mentor_id, owner_id')
      .eq('id', communityId)
      .single();

    if (findError || !communityData) {
      console.error('❌ [updateCommunity] Comunidade não encontrada:', findError);
      throw new Error('Comunidade não encontrada');
    }

    // Verificar se o usuário é o dono
    const isOwner = communityData.mentor_id === mentorId || communityData.owner_id === mentorId;
    if (!isOwner) {
      console.error('❌ [updateCommunity] Usuário não é dono da comunidade');
      throw new Error('Você não tem permissão para editar esta comunidade');
    }

    // Primeiro atualizar a comunidade
    const { error: updateError } = await supabase
      .from('communities')
      .update(updateData)
      .eq('id', communityId);

    if (updateError) {
      console.error('❌ [updateCommunity] Erro ao atualizar:', updateError);
      console.error('❌ [updateCommunity] updateData enviado:', updateData);
      throw new Error(`Falha ao atualizar comunidade: ${updateError.message}`);
    }

    // Depois buscar os dados atualizados
    const { data, error } = await supabase
      .from('communities')
      .select('*')
      .eq('id', communityId)
      .single();

    if (error) {
      console.error('❌ [updateCommunity] Erro ao buscar dados atualizados:', error);
      throw new Error('Falha ao buscar dados atualizados da comunidade');
    }

    console.log('✅ [updateCommunity] Comunidade atualizada:', data);
    return data as Community;
  } catch (error) {
    console.error('❌ [updateCommunity] Erro geral:', error);
    throw error;
  }
};

// ===============================
// DELETAR COMUNIDADE
// ===============================

export const deleteCommunity = async (
  communityId: string,
  mentorId: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('communities')
      .delete()
      .eq('id', communityId)
      .eq('owner_id', mentorId);

    if (error) {
      console.error('Erro ao deletar comunidade:', error);
      throw new Error('Falha ao deletar comunidade');
    }
  } catch (error) {
    console.error('Erro ao deletar comunidade:', error);
    throw error;
  }
};

// ===============================
// BUSCAR COMUNIDADE DO MENTOR POR ID
// ===============================

export const fetchMentorCommunityById = async (
  communityId: string,
  mentorId: string
): Promise<CommunityWithDetails | null> => {
  console.log('🔍 [fetchMentorCommunityById] Buscando comunidade com ID:', communityId, 'mentor:', mentorId);
  
  try {
    // Validar parâmetros
    if (!communityId || !mentorId) {
      console.error('❌ [fetchMentorCommunityById] Parâmetros inválidos:', { communityId, mentorId });
      return null;
    }

    // Primeiro buscar a comunidade pelo ID
    console.log('🔍 [fetchMentorCommunityById] Buscando comunidade na base...');
    const { data: communityData, error: communityError } = await supabase
      .from('communities')
      .select('*')
      .eq('id', communityId)
      .single();

    if (communityError) {
      console.error('❌ [fetchMentorCommunityById] Erro ao buscar comunidade:', communityError);
      return null;
    }

    if (!communityData) {
      console.error('❌ [fetchMentorCommunityById] Comunidade não encontrada');
      return null;
    }

    console.log('✅ [fetchMentorCommunityById] Comunidade encontrada:', communityData);

    // Verificar se o usuário é o dono (mentor_id ou owner_id)
    const isOwner = communityData.mentor_id === mentorId || communityData.owner_id === mentorId;
    console.log('🔍 [fetchMentorCommunityById] Verificação de propriedade:', {
      mentorId,
      community_mentor_id: communityData.mentor_id,
      community_owner_id: communityData.owner_id,
      isOwner
    });

    if (!isOwner) {
      console.error('❌ [fetchMentorCommunityById] Usuário não é dono da comunidade');
      return null;
    }

    // Buscar dados do jogo
    console.log('🔍 [fetchMentorCommunityById] Buscando dados do jogo:', communityData.game_id);
    let gameData = null;
    try {
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select('id, name, cover_url')
        .eq('id', communityData.game_id)
        .single();
      
      if (gameError) {
        console.warn('⚠️ [fetchMentorCommunityById] Erro ao buscar jogo:', gameError);
      } else {
        gameData = game;
        console.log('✅ [fetchMentorCommunityById] Jogo encontrado:', gameData);
      }
    } catch (error) {
      console.warn('⚠️ [fetchMentorCommunityById] Jogo não encontrado:', error);
    }

    // Buscar dados do mentor
    console.log('🔍 [fetchMentorCommunityById] Buscando dados do mentor:', mentorId);
    const mentorData = await fetchMentorProfile(mentorId);
    console.log('✅ [fetchMentorCommunityById] Dados do mentor:', mentorData);

    const result: CommunityWithDetails = {
      ...communityData,
      game: gameData || { id: communityData.game_id, name: 'Jogo Desconhecido' },
      mentor: mentorData
    };

    console.log('✅ [fetchMentorCommunityById] Resultado completo:', result);
    return result;

  } catch (error) {
    console.error('❌ [fetchMentorCommunityById] Erro geral:', error);
    return null;
  }
};

// ===============================
// VALIDAR SLUG ÚNICO
// ===============================

export const validateCommunitySlug = async (
  slug: string,
  excludeId?: string
): Promise<boolean> => {
  console.log('🔍 [validateCommunitySlug] Validando slug:', { slug, excludeId });
  
  try {
    let query = supabase
      .from('communities')
      .select('id')
      .eq('slug', slug);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query.maybeSingle(); // Use maybeSingle em vez de single

    console.log('🔍 [validateCommunitySlug] Resultado:', { data, error });

    if (error) {
      console.error('❌ [validateCommunitySlug] Erro ao validar slug:', error);
      return false;
    }

    const isAvailable = !data; // true se não encontrou (slug disponível)
    console.log('✅ [validateCommunitySlug] Slug disponível:', isAvailable);
    return isAvailable;
  } catch (error) {
    console.error('❌ [validateCommunitySlug] Erro geral:', error);
    return false;
  }
};

// Nova função para gerar slug único
export const generateUniqueSlug = async (baseName: string): Promise<string> => {
  console.log('🔧 [generateUniqueSlug] Gerando slug único para:', baseName);
  
  // Gerar slug base
  const baseSlug = baseName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
    .replace(/\s+/g, '-') // Substitui espaços por hífens
    .replace(/-+/g, '-') // Remove hífens duplos
    .replace(/^-|-$/g, ''); // Remove hífens do início e fim

  // Verificar se o slug base está disponível
  const isBaseAvailable = await validateCommunitySlug(baseSlug);
  if (isBaseAvailable) {
    console.log('✅ [generateUniqueSlug] Slug base disponível:', baseSlug);
    return baseSlug;
  }

  // Se não estiver disponível, tentar com sufixos numéricos
  for (let i = 2; i <= 999; i++) {
    const slugWithSuffix = `${baseSlug}-${i}`;
    const isAvailable = await validateCommunitySlug(slugWithSuffix);
    
    if (isAvailable) {
      console.log('✅ [generateUniqueSlug] Slug único gerado:', slugWithSuffix);
      return slugWithSuffix;
    }
  }

  // Último recurso: adicionar timestamp
  const timestampSlug = `${baseSlug}-${Date.now()}`;
  console.log('⚠️ [generateUniqueSlug] Usando timestamp como último recurso:', timestampSlug);
  return timestampSlug;
};

// ===============================
// ESTATÍSTICAS DA COMUNIDADE
// ===============================

export const fetchCommunityStats = async (
  communityId: string,
  mentorId: string
): Promise<{
  memberCount: number;
  postCount: number;
  likeCount: number;
  commentCount: number;
  recentMembers: any[];
  popularPosts: any[];
}> => {
  console.log('📊 [fetchCommunityStats] Buscando estatísticas para comunidade:', communityId);
  
  try {
    // Primeiro verificar se o mentor é dono da comunidade
    const { data: communityData, error: communityError } = await supabase
      .from('communities')
      .select('id, mentor_id, owner_id')
      .eq('id', communityId)
      .single();

    if (communityError || !communityData) {
      throw new Error('Comunidade não encontrada');
    }

    const isOwner = communityData.mentor_id === mentorId || communityData.owner_id === mentorId;
    if (!isOwner) {
      throw new Error('Sem permissão para ver estatísticas desta comunidade');
    }

    // Inicializar estatísticas
    const stats = {
      memberCount: 0,
      postCount: 0,
      likeCount: 0,
      commentCount: 0,
      recentMembers: [],
      popularPosts: []
    };

    // 1. Buscar número de membros da comunidade
    console.log('👥 [fetchCommunityStats] Buscando membros...');
    try {
      const { count: memberCount, error: memberError } = await supabase
        .from('community_memberships')
        .select('*', { count: 'exact', head: true })
        .eq('community_id', communityId);

      if (memberError) {
        console.warn('⚠️ [fetchCommunityStats] Erro ao buscar membros:', memberError);
      } else {
        stats.memberCount = memberCount || 0;
        console.log('✅ [fetchCommunityStats] Membros encontrados:', stats.memberCount);
      }
    } catch (error) {
      console.warn('⚠️ [fetchCommunityStats] Tabela community_members pode não existir:', error);
    }

    // 2. Buscar número de posts
    console.log('📝 [fetchCommunityStats] Buscando posts...');
    try {
      const { count: postCount, error: postError } = await supabase
        .from('community_posts')
        .select('*', { count: 'exact', head: true })
        .eq('community_id', communityId);

      if (postError) {
        console.warn('⚠️ [fetchCommunityStats] Erro ao buscar posts:', postError);
      } else {
        stats.postCount = postCount || 0;
        console.log('✅ [fetchCommunityStats] Posts encontrados:', stats.postCount);
      }
    } catch (error) {
      console.warn('⚠️ [fetchCommunityStats] Erro inesperado ao buscar posts:', error);
    }

    // 3. Buscar total de likes nos posts da comunidade
    console.log('❤️ [fetchCommunityStats] Buscando likes...');
    try {
      const { data: postsData, error: postsError } = await supabase
        .from('community_posts')
        .select('likes_count')
        .eq('community_id', communityId);

      if (postsError) {
        console.warn('⚠️ [fetchCommunityStats] Erro ao buscar likes:', postsError);
      } else {
        stats.likeCount = postsData?.reduce((total, post) => total + (post.likes_count || 0), 0) || 0;
        console.log('✅ [fetchCommunityStats] Likes encontrados:', stats.likeCount);
      }
    } catch (error) {
      console.warn('⚠️ [fetchCommunityStats] Erro inesperado ao buscar likes:', error);
    }

    // 4. Buscar número de comentários
    console.log('💬 [fetchCommunityStats] Buscando comentários...');
    try {
      // Buscar posts da comunidade primeiro
      const { data: communityPosts, error: communityPostsError } = await supabase
        .from('community_posts')
        .select('id')
        .eq('community_id', communityId);

      if (communityPostsError) {
        console.warn('⚠️ [fetchCommunityStats] Erro ao buscar posts para comentários:', communityPostsError);
      } else if (communityPosts && communityPosts.length > 0) {
        const postIds = communityPosts.map(post => post.id);
        
        const { count: commentCount, error: commentError } = await supabase
          .from('community_post_comments')
          .select('*', { count: 'exact', head: true })
          .in('post_id', postIds);

        if (commentError) {
          console.warn('⚠️ [fetchCommunityStats] Erro ao buscar comentários:', commentError);
        } else {
          stats.commentCount = commentCount || 0;
          console.log('✅ [fetchCommunityStats] Comentários encontrados:', stats.commentCount);
        }
      }
    } catch (error) {
      console.warn('⚠️ [fetchCommunityStats] Erro inesperado ao buscar comentários:', error);
    }

    // 5. Buscar membros recentes (últimos 5)
    console.log('👤 [fetchCommunityStats] Buscando membros recentes...');
    try {
      const { data: recentMembers, error: recentMembersError } = await supabase
        .from('community_memberships')
        .select(`
          created_at,
          profiles:user_id(id, nickname, full_name)
        `)
        .eq('community_id', communityId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (recentMembersError) {
        console.warn('⚠️ [fetchCommunityStats] Erro ao buscar membros recentes:', recentMembersError);
      } else {
        stats.recentMembers = recentMembers || [];
        console.log('✅ [fetchCommunityStats] Membros recentes encontrados:', stats.recentMembers.length);
      }
    } catch (error) {
      console.warn('⚠️ [fetchCommunityStats] Erro inesperado ao buscar membros recentes:', error);
    }

    // 6. Buscar posts populares (top 5 por likes)
    console.log('🔥 [fetchCommunityStats] Buscando posts populares...');
    try {
      const { data: popularPosts, error: popularPostsError } = await supabase
        .from('community_posts')
        .select('id, title, likes_count, created_at')
        .eq('community_id', communityId)
        .order('likes_count', { ascending: false })
        .limit(5);

      if (popularPostsError) {
        console.warn('⚠️ [fetchCommunityStats] Erro ao buscar posts populares:', popularPostsError);
      } else {
        stats.popularPosts = popularPosts || [];
        console.log('✅ [fetchCommunityStats] Posts populares encontrados:', stats.popularPosts.length);
      }
    } catch (error) {
      console.warn('⚠️ [fetchCommunityStats] Erro inesperado ao buscar posts populares:', error);
    }

    console.log('✅ [fetchCommunityStats] Estatísticas completas:', stats);
    return stats;

  } catch (error) {
    console.error('❌ [fetchCommunityStats] Erro ao buscar estatísticas:', error);
    throw error;
  }
}; 